import type { ReactNode } from 'react'
import { OrgSelector } from './OrgSelector'

// =============================================================================
// AccountPageHeader
//
// One consistent page-header pattern across every /account/* surface.
// Title left, optional action + ORGANIZATION switcher right. Subtitle
// (if any) sits below the title row. The org switcher is the same
// shared OrgSelector — same label, same chrome, same position — so the
// user sees a stable "current org" anchor as they navigate between
// Account / Keys / Organization pages.
//
// Action slot: optional content rendered immediately to the left of
// the org switcher in the top-right cluster. Used by the Organization
// page for the "+ New organization" affordance.
// =============================================================================

interface AccountPageHeaderProps {
  title: string
  subtitle?: ReactNode
  action?: ReactNode
  orgId: string
  onOrgChange: (next: string) => void | Promise<void>
}

export function AccountPageHeader({ title, subtitle, action, orgId, onOrgChange }: AccountPageHeaderProps) {
  return (
    <header className="mb-8">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-[26px] font-semibold tracking-tight text-[var(--color-text-strong)]">
          {title}
        </h1>
        <div className="flex items-center gap-3 flex-wrap">
          {action}
          <OrgSelector value={orgId} onChange={onOrgChange} />
        </div>
      </div>
      {subtitle ? (
        <p className="mt-2 text-[14px] text-[var(--color-muted)]">{subtitle}</p>
      ) : null}
    </header>
  )
}
